import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Card } from '~/eds/src/components/Card/Card';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Section } from '~/eds/src/components/Section/Section';
import { Text } from '~/eds/src/components/Text/Text';
import { useUuid } from '~/eds/src/hooks/useUuid';
import { EntityType } from '~/enums/EntityType';
import { QueryFiltersFormField } from '~/features/advanced-search/query-filters/QueryFiltersFormField';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import { PUBLISH_ELEMENT_ID, SCOPE_THRESHOLD_WARNING } from '../constants';
import { FieldModel } from '../fields/types';
import { DocumentsInScope } from '../ModelDetails/Configuration/DocumentsInScope';
import { useGetDocumentCount } from '../ModelDetails/hooks';
import {
  testEnablePublish,
  testIsNonOptimizedFieldModel,
} from '../utils/status';

interface Props {
  isVisible: boolean;
  fieldModel: FieldModel;
  onCancel: () => void;
}
export const PublishQuotaFieldModelModal = ({
  isVisible,
  fieldModel,
  onCancel,
}: Props) => {
  const [id] = useUuid();

  const { toast } = useToast();

  const dispatch = useDispatch();

  const { navigate } = useRouting();

  const intl = useIntl();

  const [publishConfig, setPublishConfig] = useState({
    enableRepublish: false,
    enableRepublishConfirm: false,
  });

  const selectPublishMode = useSelector(
    slices.xRay.selectors.selectPublishMode,
  );

  const field = useSelector(slices.xRay.selectors.selectField);

  const draft = fieldModel && fieldModel.publishStatus === null;

  const query = useSelector(slices.xRay.selectors.selectQuery);

  const suspended =
    fieldModel && testIsNonOptimizedFieldModel(fieldModel, ['suspended']);

  const enablePublish = testEnablePublish(publishConfig);

  useEffect(() => {
    if (draft && isVisible) {
      showPublishModal();
    } else if (suspended && isVisible) {
      showRepublishModal();
    }
  }, [draft, suspended, isVisible]);

  useEffect(() => {
    if (suspended) {
      dispatch(
        slices.xRay.actions.setPublishMode(
          publishConfig.enableRepublishConfirm
            ? 'all_docs_in_scope'
            : 'new_docs_in_scope',
        ),
      );
    }
  }, [draft, suspended, publishConfig]);

  const {
    data: progress = {
      docsProcessed: 0,
      docsInScope: 0,
    },
    isLoading: isProgressLoading,
    isFetching: isProgressFetching,
  } = api.endpoints.getPromptModelProgress.useQuery({
    modelId: fieldModel.id,
  });

  const docsUnprocessed = progress.docsInScope - progress.docsProcessed;

  const { docsInScope, isLoadingDocsInScope } = useGetDocumentCount({
    entityId: field?.id ? String(field.id) : null,
    entityType: EntityType.Field,
    query: fieldModel.config.query,
  });

  const hasScopeWarning = docsInScope > SCOPE_THRESHOLD_WARNING;

  const [publishFieldModel, { isLoading: isPublishingFieldModel }] =
    api.endpoints.publishPromptModel.useMutation();

  const handlePublish = () => {
    publishFieldModel({
      modelId: fieldModel.id,
      version: fieldModel.version,
      idempotencyKey: id,
      publishMode: selectPublishMode,
    })
      .unwrap()
      .then(
        () => {
          toast({
            message: intl.formatMessage(
              {
                id: 'PublishQuotaModal.publishSuccessMessage',
                defaultMessage:
                  '"{modelName}" is active and is processing documents.',
                description:
                  'Success message when model is published and processing documents. Example: modelName = "Document Name"',
              },
              {
                modelName: fieldModel!.name,
              },
            ),
            status: 'success',
          });
          dispatch(slices.xRay.actions.resetXRay());
          dispatch(
            slices.xRay.actions.setActiveModelResultInfo({
              id: fieldModel.id,
              latestVersion: fieldModel.version,
              targetEntityDetails: {
                id: '', // not needed
                label: fieldModel.name,
                type: EntityType.Field,
              },
            }),
          );
          navigate(`${RoutePathType.AutomationHubFields}`);
        },
        () =>
          toast({
            message: intl.formatMessage({
              id: 'PublishQuotaModal.publishErrorMessage',
              defaultMessage:
                'There was an error publishing the model. Please try again.',
              description: 'Error message when model publishing fails',
            }),
            status: 'danger',
          }),
      );
  };

  const handleOnHide = () => {
    setPublishConfig({
      ...publishConfig,
      enableRepublish: false,
    });

    onCancel();
  };

  const scope = (
    <>
      <QueryFiltersFormField
        mode="preview"
        readOnly
        optional={false}
        query={fieldModel.config.query ?? []}
        onQueryChange={() => {}}
        title={intl.formatMessage({
          id: 'PublishQuotaModal.currentScopeTitle',
          defaultMessage: 'Current Scope',
          description: 'Title for current scope section',
        })}
      />
      <DocumentsInScope
        query={query}
        entityId={field?.id ? String(field.id) : null}
        entityType={EntityType.Field}
      />
    </>
  );

  const [confirmPublishModal, showPublishModal] = useModal({
    children: (
      <Layout id={PUBLISH_ELEMENT_ID} preset="sections">
        {hasScopeWarning ? (
          <Card mode="bordered" status="warning">
            <Layout preset="sections">
              {scope}
              <Text>
                <FormattedMessage
                  id="PublishQuotaModal.scopeWarningMessage"
                  defaultMessage="The current scope selection will take a long time to run. Consider refining your selection before continuing."
                  description="Warning message for large scope selection"
                />
              </Text>
            </Layout>
          </Card>
        ) : (
          scope
        )}
        <Section
          title={intl.formatMessage({
            id: 'PublishQuotaModal.publishModelSectionTitle',
            defaultMessage: 'Publish model',
            description: 'Section title for publish model',
          })}
        >
          <Text>
            <FormattedMessage
              id="PublishQuotaModal.quotaConfirmationMessage"
              defaultMessage="You're about to publish a model. This action will affect your account balance."
              description="Message asking for quota consumption confirmation"
            />
          </Text>
        </Section>
      </Layout>
    ),
    loadingContent: {
      isLoading: isLoadingDocsInScope,
      message: intl.formatMessage({
        id: 'PublishQuotaModal.loadingContentMessage',
        defaultMessage: 'Loading content…',
        description: 'Loading message for content',
      }),
    },
    title: intl.formatMessage({
      id: 'PublishQuotaModal.confirmPublishTitle',
      defaultMessage: 'Confirm to Publish',
      description: 'Modal title for confirming model publishing',
    }),
    primaryAction: {
      isLoading: isPublishingFieldModel,
      text: intl.formatMessage({
        id: 'PublishQuotaModal.publishButton',
        defaultMessage: 'Publish',
        description: 'Publish button text',
      }),
      onClick: handlePublish,
    },

    onHide: handleOnHide,
  });

  const [confirmRepublishModal, showRepublishModal] = useModal({
    children: (
      <Layout id={PUBLISH_ELEMENT_ID} preset="sections">
        {scope}
        <Section
          title={intl.formatMessage({
            id: 'PublishQuotaModal.republishModelSectionTitle',
            defaultMessage: 'Re-publish model',
            description: 'Section title for re-publish model',
          })}
        >
          <Markdown
            text={intl.formatMessage(
              {
                id: 'PublishQuotaModal.republishDescription',
                defaultMessage:
                  'This applies edits to <strong>{docsUnprocessed} unprocessed documents</strong>. Editing and re-publishing models <strong>will not consume</strong> another model quota.',
                description:
                  'Description of republishing model and quota usage. Example: docsUnprocessed = "example"',
              },
              {
                docsUnprocessed,
                strong: (chunks) => `<strong>${chunks}</strong>`,
              },
            )}
          />
        </Section>
        <Checkbox
          name="publish-mode"
          option={{
            label: intl.formatMessage({
              id: 'PublishQuotaModal.republishAllDocumentsLabel',
              defaultMessage:
                'I want to re-publish on all documents (not recommended)',
              description: 'Checkbox label for republishing on all documents',
            }),
            value: 'all_docs_in_scope',
          }}
          onChange={(value) => {
            if (value === false) {
              setPublishConfig({
                ...publishConfig,
                enableRepublish: value ?? false,
                enableRepublishConfirm: false,
              });
            } else {
              setPublishConfig({
                ...publishConfig,
                enableRepublish: value ?? false,
              });
            }
          }}
          value={publishConfig.enableRepublish}
        />
        {publishConfig.enableRepublish && (
          <Card mode="bordered" status={hasScopeWarning ? 'warning' : 'info'}>
            <Layout preset="sections">
              <Section
                title={intl.formatMessage({
                  id: 'PublishQuotaModal.republishAllDocumentsSectionTitle',
                  defaultMessage: 'Re-publish model on all documents in scope',
                  description:
                    'Section title for republishing on all documents',
                })}
              >
                <Markdown
                  text={
                    hasScopeWarning
                      ? intl.formatMessage(
                          {
                            id: 'PublishQuotaModal.republishAllDocumentsWarning',
                            defaultMessage:
                              'This applies edits to an additional <strong>{docsProcessed} documents</strong> in scope, including already processed documents and may take a significant amount of time. The new answers will overwrite existing answers found by Custom AI.',
                            description:
                              'Warning message for republishing on all documents with scope warning. Example: docsProcessed = "200"',
                          },
                          {
                            docsProcessed: progress.docsProcessed,
                            strong: (chunks) => `<strong>${chunks}</strong>`,
                          },
                        )
                      : intl.formatMessage(
                          {
                            id: 'PublishQuotaModal.republishAllDocumentsInfo',
                            defaultMessage:
                              'This applies edits to an additional <strong>{docsProcessed} documents</strong> in scope. The new answers will overwrite existing answers found by Custom AI.',
                            description:
                              'Info message for republishing on all documents. Example: docsProcessed = "example"',
                          },
                          {
                            docsProcessed: progress.docsProcessed,
                            strong: (chunks) => `<strong>${chunks}</strong>`,
                          },
                        )
                  }
                />
              </Section>
              <Checkbox
                name="publish-mode"
                option={{
                  label: intl.formatMessage({
                    id: 'PublishQuotaModal.republishAllDocumentsConfirmLabel',
                    defaultMessage:
                      'Yes I want to re-publish on All Documents.',
                    description:
                      'Checkbox label for confirming republish on all documents',
                  }),
                  value: 'all_docs_in_scope',
                }}
                onChange={(value) => {
                  setPublishConfig({
                    ...publishConfig,
                    enableRepublishConfirm: value ?? false,
                  });
                }}
                value={publishConfig.enableRepublishConfirm}
              />
            </Layout>
          </Card>
        )}
      </Layout>
    ),
    isVisible: isVisible,
    loadingContent: {
      isLoading:
        isProgressLoading || isLoadingDocsInScope || isProgressFetching,
      message: intl.formatMessage({
        id: 'PublishQuotaModal.loadingProgressMessage',
        defaultMessage: 'Loading progress...',
        description: 'Loading message for progress',
      }),
    },
    title: intl.formatMessage({
      id: 'PublishQuotaModal.confirmRepublishTitle',
      defaultMessage: 'Confirm to Re-publish',
      description: 'Modal title for confirming model republishing',
    }),
    primaryAction: {
      disabled: !enablePublish || isProgressLoading || isLoadingDocsInScope,
      tooltip: !enablePublish
        ? intl.formatMessage({
            id: 'PublishQuotaModal.republishTooltip',
            defaultMessage: 'Please confirm you want to re-publish.',
            description: 'Tooltip when republish not confirmed',
          })
        : undefined,
      isLoading: isPublishingFieldModel,
      text: intl.formatMessage({
        id: 'PublishQuotaModal.republishButton',
        defaultMessage: 'Re-publish',
        description: 'Re-publish button text',
      }),
      onClick: handlePublish,
    },
    onHide: handleOnHide,
  });

  return (
    <>
      {confirmPublishModal}
      {confirmRepublishModal}
    </>
  );
};
